/**
 * Story 057 — TanStack Query hooks for the turbines feature.
 *
 * Mirrors `apps/web/src/features/sites/queries/hooks.ts` shape. Reuses
 * the `assetsFetch` helper from Sites (same `/api/assets/v1` namespace).
 * Per `tanstack-query-conventions` skill: hierarchical keys + per-query
 * `staleTime` + zero retries on mutations.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { assetsFetch } from "../../sites/queries/assetsFetch.js";
import { turbinesKeys } from "./keys.js";
import type {
  CreateTurbineBody,
  ListAssetModelsResponse,
  ListTurbinesParams,
  ListTurbinesResponse,
  TurbineDetail,
  UpdateTurbineBody,
} from "./types.js";

/**
 * Translate the FE-internal sort column id (matches the DataGrid
 * `field`) into the BE wire key from `assets/v1@1.1.0` listTurbines
 * `sort` enum. The only divergence today is `internalParkName` → `name`
 * (BE-side sort expression is
 * `COALESCE(a.internal_park_number, '#' || a.asset_code)`).
 */
function turbineSortToBeWire(sort: string | undefined): string | undefined {
  if (!sort) return undefined;
  const [k, d] = sort.split(":");
  if (!k) return undefined;
  const dir = d === "desc" ? "desc" : "asc";
  const beKey = k === "internalParkName" ? "name" : k;
  return `${beKey}:${dir}`;
}

function listTurbinesUrl(params: ListTurbinesParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  if (params.search && params.search.length > 0) {
    sp.set("search", params.search);
  }
  const beSort = turbineSortToBeWire(params.sort);
  if (beSort) sp.set("sort", beSort);
  if (params.includeInactive === true) sp.set("includeInactive", "true");
  // Type CSV — emit only when at least one model is selected. Sort
  // alphabetically for deterministic URLs (matches Sites' country CSV).
  if (params.type && params.type.length > 0) {
    const sorted = [...params.type].sort((a, b) => a.localeCompare(b));
    sp.set("type", sorted.join(","));
  }
  if (params.site && params.site.length > 0) {
    const sorted = [...params.site].sort((a, b) => a.localeCompare(b));
    sp.set("site", sorted.join(","));
  }
  return `/turbines?${sp.toString()}`;
}

export function useTurbinesListQuery(params: ListTurbinesParams) {
  return useQuery<ListTurbinesResponse>({
    queryKey: turbinesKeys.list(params),
    queryFn: () => assetsFetch<ListTurbinesResponse>(listTurbinesUrl(params)),
    staleTime: 30_000,
    retry: false,
    // Keep the previous data on refetch so the DataGrid stays
    // mounted across sort / filter / page transitions — without
    // this, every URL change flips `isPending` true and the
    // <DataTable> unmounts (replaced by a spinner), which detaches
    // the column headers and breaks chained `parkHeader.click()`
    // tests like AC4.
    placeholderData: keepPreviousData,
  });
}

/**
 * FB-04 — find-or-create turbine typeahead within a chosen park.
 *
 * Server-side search for existing turbines under a single site:
 * `GET /turbines?site=<siteId>&search=<text>&pageSize=50` (the BE
 * `search` param is an indexed ILIKE over identifier / internal park
 * number). Used by the Create-Project manual-attach turbine row so the
 * user can load an existing turbine instead of always creating one.
 *
 * FB-16a/FB-17 — `enabled` now gates ONLY on a non-empty `siteId` (a NEW
 * park has no id yet → no existing turbines to search). The previous
 * `trimmed.length > 0` gate is dropped so a freshly-opened dropdown over an
 * EXISTING park lists the park's turbines immediately (empty `search` =
 * the BE returns the site's turbines), without the user having to type a
 * first character.
 */
export function useTurbinesSearchQuery(
  siteId: string | null | undefined,
  query: string,
) {
  const trimmed = query.trim();
  const hasSite = typeof siteId === "string" && siteId.length > 0;
  return useQuery<ListTurbinesResponse>({
    queryKey: [
      ...turbinesKeys.lists(),
      "__search__",
      siteId ?? "",
      trimmed,
    ] as const,
    queryFn: () =>
      assetsFetch<ListTurbinesResponse>(
        listTurbinesUrl({
          page: 1,
          pageSize: 50,
          search: trimmed,
          ...(hasSite ? { site: [siteId] } : {}),
        }),
      ),
    enabled: hasSite,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * FB-17 — bulk-add an existing park's turbines. Lists ALL turbines under a
 * single site in one shot (empty `search` = the BE returns the site's
 * turbines), with a generously-raised `pageSize` so even a large park is
 * captured in one request. Used by the create-wizard park card to auto-add
 * every existing turbine on park-resolve (the common "inspect the whole
 * park" case ≈ 1 click).
 *
 * `enabled` gates on a non-empty `siteId` AND the caller's own `enabled`
 * flag, so the fetch only fires once the park has resolved to an existing
 * site and the card actually wants the list.
 */
export function useSiteTurbinesQuery(
  siteId: string | null | undefined,
  enabled = true,
) {
  const hasSite = typeof siteId === "string" && siteId.length > 0;
  return useQuery<ListTurbinesResponse>({
    queryKey: [...turbinesKeys.lists(), "__site_all__", siteId ?? ""] as const,
    queryFn: () =>
      assetsFetch<ListTurbinesResponse>(
        listTurbinesUrl({
          page: 1,
          pageSize: 1000,
          ...(hasSite ? { site: [siteId] } : {}),
        }),
      ),
    enabled: enabled && hasSite,
    staleTime: 30_000,
    retry: false,
  });
}

export function useTurbineDetailQuery(id: string | null | undefined) {
  return useQuery<TurbineDetail>({
    queryKey: turbinesKeys.detail(id ?? "__none__"),
    queryFn: () => assetsFetch<TurbineDetail>(`/turbines/${id ?? ""}`),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useCreateTurbineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTurbineBody) =>
      assetsFetch<TurbineDetail>("/turbines", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: turbinesKeys.lists() });
    },
  });
}

export interface UpdateTurbineArgs {
  id: string;
  body: UpdateTurbineBody;
}

export function useUpdateTurbineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateTurbineArgs) =>
      assetsFetch<TurbineDetail>(`/turbines/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: turbinesKeys.lists() });
      void qc.invalidateQueries({ queryKey: turbinesKeys.detail(updated.id) });
    },
  });
}

export function useDeactivateTurbineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<TurbineDetail>(`/turbines/${id}`, { method: "DELETE" }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: turbinesKeys.lists() });
      void qc.invalidateQueries({ queryKey: turbinesKeys.detail(updated.id) });
    },
  });
}

export function useReactivateTurbineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<TurbineDetail>(`/turbines/${id}/reactivate`, {
        method: "POST",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: turbinesKeys.lists() });
      void qc.invalidateQueries({ queryKey: turbinesKeys.detail(updated.id) });
    },
  });
}

/**
 * Spec 062 — listAssetModels. When `assetTypeId` is provided, returns
 * the cascading-dropdown projection scoped to that type. When omitted,
 * returns ALL permitted models for the tenant (used by the Type filter
 * in the toolbar).
 *
 * The response carries BOTH the tenant's permitted models
 * (`scope: 'tenant'`) AND the rest of the global catalog
 * (`scope: 'global'`) in a single flat `items[]` array. The FE groups
 * the Autocomplete options into "Frequently used" / "All models"
 * sections (Christian's Q5 lock 2026-05-13).
 */
export function useAssetModelsForTypeQuery(
  assetTypeId: string | null | undefined,
) {
  return useQuery<ListAssetModelsResponse>({
    queryKey: turbinesKeys.assetModels(assetTypeId ?? null),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (assetTypeId) sp.set("assetTypeId", assetTypeId);
      const qs = sp.toString();
      return assetsFetch<ListAssetModelsResponse>(
        `/asset-models${qs.length > 0 ? `?${qs}` : ""}`,
      );
    },
    // Catalog data is highly cacheable; refetch is cheap but the user
    // typically picks one model and moves on.
    staleTime: 5 * 60_000,
    retry: false,
  });
}
